import "./styles.css";

import {
  useLoaderData,
  useLocation,
  useNavigate,
  useNavigation,
  useRouteError,
} from "react-router-dom";
import GenericErrorDisplay, { errorToString } from "../generic-error";
import ResourceService from "../../service/resource";
import useAuth, { useIsAuth, useLogin } from "../../auth/hooks";
import OutputResource from "../../dtos/resource";
import { useEffect, useRef, useState } from "react";
import { IMAGE_HOST, RECAPTCHA_SITE_KEY } from "../../server/config";
import ResourceDisplay from "../../components/resource-display";
import TextInput from "../../components/inputs/text";
import UserService from "../../service/user";
import { AxiosError } from "axios";
import { CircularProgress, IconButton, InputAdornment } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import LcService from "../../service/lc";
import { PublicLc } from "../../dtos/lc";
import DropdownInput from "../../components/inputs/radio/dropdown";
import NumberInput from "../../components/inputs/number";
import SwitchInput from "../../components/inputs/checkbox/switch";
import FileInput from "../../components/inputs/file";
import { path as accountPath } from "../page-account";
import useWindowDimensions from "../../hooks/useWindowDimensions";
import ReCAPTCHA from "react-google-recaptcha";

export const path = "/login/";

const resourceKeys = {
  bg: "login-bg",
  element: "login-element",
  buttonText: "login-button-text",
};

type LoaderType = [{ [key: string]: OutputResource }, PublicLc[]];

export async function loader(): Promise<LoaderType> {
  const resources = new ResourceService().read.byIds(
    Object.values(resourceKeys),
  );
  const lcs = new LcService().read.all();
  return [await resources, await lcs];
}

export function Component() {
  const service = new UserService();
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const isAuth = useIsAuth();
  const auth = useAuth();
  const login = useLogin();

  const [resources, lcs] = useLoaderData() as LoaderType;
  const navigate = useNavigate();
  const location = useLocation();
  const navigation = useNavigation();
  const { width } = useWindowDimensions();

  useEffect(() => {
    if (isAuth() || auth !== null) {
      const queryParams = new URLSearchParams(location.search);
      const redirect = queryParams.get("redirect");
      navigate(redirect !== null ? redirect : accountPath);
    }
  }, [isAuth, auth, navigate, location]);

  const [publicId, setPublicId] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [passwordConfirmation, setPasswordConfirmation] = useState<string>("");
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [lcId, setLcId] = useState<string>("");
  const [year, setYear] = useState<number | null>(null);
  const [medicine, setMedicine] = useState<boolean>(true);
  const [programmeTitle, setProgrammeTitle] = useState<string>("");
  const [series, setSeries] = useState<number | null>(null);
  const [group, setGroup] = useState<number | null>(null);
  const [registrationNumber, setRegistrationNumber] = useState<string>("");
  const [idImage, setIdImage] = useState<File | null>(null);
  const [otp, setOtp] = useState<string>("");

  const [otpAction, setOtpAction] = useState<string | null>(null);
  const [forgotPassword, setForgotPassword] = useState<boolean>(false);

  const [errorMessage, setErrorMessage] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [passwordShown, setPasswordShown] = useState<boolean>(false);
  const [isRegister, setIsRegister] = useState<boolean>(false);

  return (
    <div
      id="page-login"
      style={{
        backgroundImage: `url(${
          IMAGE_HOST + resources[resourceKeys.bg].value
        })`,
        backgroundColor: "var(--color-light)",
        display: "flex",
        flexDirection: width > 800 ? "row" : "column",
        flexWrap: "wrap",
        alignItems: width > 800 ? "center" : "stretch",
        padding: width > 800 ? "var(--gap-xl)" : "var(--gap-small)",
        gap: "var(--gap-xl)",
        color: "var(--color-dark)",
      }}
    >
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          gap: "var(--gap-small)",
          width: "100%",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "stretch",
            justifyContent: "stretch",
          }}
        >
          <button
            className={
              "switch button primary " + (isRegister ? "text" : "outlined")
            }
            onClick={() => setIsRegister(false)}
          >
            LOG IN
          </button>
          <div
            style={{
              width: "1px",
              backgroundColor: "var(--color-gray)",
              margin: "var(--gap-small)",
            }}
          />
          <button
            className={
              "switch button primary " + (isRegister ? "outlined" : "text")
            }
            onClick={() => setIsRegister(true)}
          >
            SIGN UP
          </button>
        </div>
        <div
          className="surface"
          style={{
            padding: 0,
            overflow: "hidden",
            width: "100%",
            display: "flex",
          }}
        >
          <div
            className="inline-scrollbar x"
            style={{
              padding: 0,
              width: "100%",
              display: "flex",
            }}
          >
            <form
              style={{
                display: "flex",
                padding: "var(--surface-padding)",
                flexDirection: "column",
                alignItems: "stretch",
                gap: "var(--gap-small)",
                flex: 1,
              }}
              onSubmit={async (event) => {
                event.preventDefault();
                setErrorMessage("");
                setLoading(true);
                try {
                  if (recaptchaRef.current === null)
                    throw new Error("Recaptcha not loaded.");

                  // const recaptchaToken =
                  //   await recaptchaRef.current.executeAsync();
                  const recaptchaToken = "";

                  if (recaptchaToken === null)
                    throw new Error("Recaptcha failed.");

                  if (otpAction !== null) {
                    await service.create.finishOtp(otpAction, otp);
                    await login(publicId, password, recaptchaToken);
                    setOtpAction(null);
                  } else {
                    if (isRegister) {
                      if (password !== passwordConfirmation)
                        throw new Error("Parolele nu coincid.");
                      setOtpAction(
                        await service.create.register(
                          recaptchaToken,
                          email,
                          publicId,
                          password,
                          firstName,
                          lastName,
                          lcId,
                          year!,
                          medicine,
                          medicine ? "Medicină" : programmeTitle,
                          series!,
                          group!,
                          registrationNumber,
                          idImage!,
                        ),
                      );
                    } else {
                      if (!forgotPassword) {
                        await login(publicId, password, recaptchaToken);
                      } else {
                        if (password !== passwordConfirmation)
                          throw new Error("Parolele nu coincid.");
                        setOtpAction(
                          await service.update.password(
                            recaptchaToken,
                            publicId,
                            password,
                          ),
                        );
                      }
                    }
                  }
                } catch (error) {
                  console.error(error);
                  if (
                    error instanceof AxiosError &&
                    error.response &&
                    error.response.status === 404
                  )
                    setErrorMessage("E-mail, id sau parolă incorecte");
                  else setErrorMessage(errorToString(error));
                } finally {
                  setLoading(false);
                  recaptchaRef.current?.reset();
                }
              }}
            >
              <h1
                style={{
                  marginBottom: "var(--gap-big)",
                }}
              >
                {isRegister ? "Înregistrează-te" : "Intră în cont"}
              </h1>
              {isRegister && (
                <TextInput
                  required
                  label="Email"
                  onChange={(event) => setEmail(event.target.value)}
                  value={email}
                  disabled={otpAction !== null}
                />
              )}
              <TextInput
                required
                label={isRegister ? "Id public" : "E-mail sau Id public"}
                onChange={(event) => setPublicId(event.target.value)}
                value={publicId}
                disabled={otpAction !== null}
              />
              <TextInput
                required
                type={passwordShown ? "" : "password"}
                label={!isRegister && forgotPassword ? "Parola nouă" : "Parola"}
                onChange={(event) => setPassword(event.target.value)}
                value={password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setPasswordShown((value) => !value)}
                      >
                        <FontAwesomeIcon
                          icon={passwordShown ? faEyeSlash : faEye}
                          size="xs"
                        />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                disabled={otpAction !== null}
              />
              {(isRegister || forgotPassword) && (
                <TextInput
                  required
                  type={passwordShown ? "" : "password"}
                  label="Confirmă parola"
                  onChange={(event) =>
                    setPasswordConfirmation(event.target.value)
                  }
                  value={passwordConfirmation}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setPasswordShown((value) => !value)}
                        >
                          <FontAwesomeIcon
                            icon={passwordShown ? faEyeSlash : faEye}
                            size="xs"
                          />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  disabled={otpAction !== null}
                />
              )}
              {isRegister && (
                <>
                  <TextInput
                    required
                    label="Prenume"
                    onChange={(event) => setFirstName(event.target.value)}
                    value={firstName}
                    disabled={otpAction !== null}
                  />
                  <TextInput
                    required
                    label="Nume de familie"
                    onChange={(event) => setLastName(event.target.value)}
                    value={lastName}
                    disabled={otpAction !== null}
                  />
                  <DropdownInput
                    label="Centru"
                    options={Object.fromEntries(
                      lcs.map((lc) => [lc.id, lc.name]),
                    )}
                    value={lcId}
                    onChange={(lc) => setLcId(lc)}
                    disabled={otpAction !== null}
                  />
                  <SwitchInput
                    label="Student la medicină"
                    checked={medicine}
                    onChange={(e) => setMedicine(e.target.checked)}
                    disabled={otpAction !== null}
                  />
                  {!medicine && (
                    <TextInput
                      required
                      label="Program de studiu"
                      onChange={(event) =>
                        setProgrammeTitle(event.target.value)
                      }
                      value={programmeTitle}
                      disabled={otpAction !== null}
                    />
                  )}
                  <NumberInput
                    required
                    label="An de studiu"
                    onChange={(e) =>
                      setYear(
                        isNaN(parseInt(e.target.value))
                          ? null
                          : parseInt(e.target.value),
                      )
                    }
                    value={year ?? ""}
                    disabled={otpAction !== null}
                  />
                  <NumberInput
                    required
                    label="Serie"
                    helperText="Dacă este o singură serie, atunci aceasta este seria 1"
                    onChange={(e) =>
                      setSeries(
                        isNaN(parseInt(e.target.value))
                          ? null
                          : parseInt(e.target.value),
                      )
                    }
                    value={series ?? ""}
                    disabled={otpAction !== null}
                  />
                  <NumberInput
                    required
                    label="Grupă"
                    helperText="Dacă este o singură grupă, atunci aceasta este grupa 1"
                    onChange={(e) =>
                      setGroup(
                        isNaN(parseInt(e.target.value))
                          ? null
                          : parseInt(e.target.value),
                      )
                    }
                    value={group ?? ""}
                    disabled={otpAction !== null}
                  />
                  <TextInput
                    required
                    label="Număr de înmatriculare"
                    onChange={(event) =>
                      setRegistrationNumber(event.target.value)
                    }
                    value={registrationNumber}
                    disabled={otpAction !== null}
                  />
                  <FileInput
                    required
                    label="Fotografie carnet student"
                    onChange={(files) =>
                      setIdImage(files.length > 0 ? (files[0] ?? null) : null)
                    }
                    types={["jpg", "jpeg", "png"]}
                    disabled={otpAction !== null}
                  />
                </>
              )}
              {otpAction !== null && (
                <TextInput
                  required
                  label="Cod de verificare"
                  helperText={
                    "Trimis la " +
                    (isRegister ? email : `email-ul contului <${publicId}>`)
                  }
                  onChange={(event) => setOtp(event.target.value)}
                  value={otp}
                />
              )}
              {errorMessage.length > 0 && (
                <div
                  style={{ color: "red" }}
                  onClick={() => setErrorMessage("")}
                >
                  {errorMessage}
                </div>
              )}
              <ReCAPTCHA
                ref={recaptchaRef}
                size="invisible"
                sitekey={RECAPTCHA_SITE_KEY}
                onErrored={() =>
                  setErrorMessage("Recaptcha failed, please reload page.")
                }
                onExpired={() => {}}
              />
              <div
                style={{
                  alignSelf: "flex-start",
                  marginTop: "var(--gap-big)",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: "var(--gap-small)",
                  color: "var(--color-fasmr-blue)",
                }}
              >
                <button
                  disabled={loading || navigation.state === "loading"}
                  type="submit"
                  className="primary button"
                >
                  {otpAction === null
                    ? isRegister
                      ? "Înregistrează-te"
                      : forgotPassword
                        ? "Trimite cod"
                        : "Intră în cont"
                    : "Verifică"}
                </button>
                {otpAction !== null && (
                  <>
                    <button
                      disabled={loading || navigation.state === "loading"}
                      type="button"
                      className="secondary button"
                      onClick={() => setOtpAction(null)}
                    >
                      Modifică
                    </button>
                    <button
                      disabled={loading || navigation.state === "loading"}
                      type="button"
                      className="primary button text"
                      onClick={async (event) => {
                        event.preventDefault();
                        setErrorMessage("");
                        setLoading(true);
                        try {
                          await service.read.newOtp(otpAction);
                        } catch (error) {
                          console.error(error);
                          if (
                            error instanceof AxiosError &&
                            error.response &&
                            error.response.status === 404
                          )
                            setErrorMessage("E-mail, id sau parolă incorecte");
                          else setErrorMessage(errorToString(error));
                        } finally {
                          setLoading(false);
                        }
                      }}
                    >
                      Cod nou
                    </button>
                  </>
                )}
                {!isRegister && (
                  <button
                    disabled={loading || navigation.state === "loading"}
                    type="button"
                    className="primary outlined button"
                    onClick={() => setForgotPassword((value) => !value)}
                  >
                    {forgotPassword ? "Log in" : "Resetează parola"}
                  </button>
                )}
                {(loading || navigation.state === "loading") && (
                  <CircularProgress />
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
      <ResourceDisplay
        resource={resources[resourceKeys.element]}
        className="element"
        style={{
          alignSelf: "flex-end",
          height: "80dvh",
          flex: 1,
          marginBottom: "calc(-1 * var(--gap-xl))",
        }}
      />
    </div>
  );
}
Component.displayName = "page-login";

export function ErrorBoundary() {
  const error = useRouteError();
  return <GenericErrorDisplay error={error} />;
}
ErrorBoundary.displayName = "page-login-error";
